/**
 * useStartMigration
 *
 * Shared logic for starting a fresh migration: reset any persisted workflow state,
 * generate a workflow code, register it as the current workflow, then navigate
 * straight to its detail page. Used by both the WorkflowPage cards and the
 * left sidebar so the two entry points behave identically.
 *
 * @module hooks/useStartMigration
 */

import { useNavigate } from "react-router-dom";
import { useGenerateCode } from "../lib/api";
import { useInstancePairStore } from "../store/useInstancePairStore";
import { useWorkflowStore } from "../store/useWorkflowStore";
import { useToast } from "../lib/use-toast";

type MigrationType = "ONTOLOGY" | "PROCESS" | "ACTION_EFFECT";

// Detail-page route segment per migration type.
const ROUTE_SEGMENT: Record<MigrationType, string> = {
  ONTOLOGY: "ontology",
  PROCESS: "process",
  ACTION_EFFECT: "action-effect",
};

export function useStartMigration() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCurrentWorkflow } = useWorkflowStore();
  const { generateCode, isGenerating } = useGenerateCode();

  const start = async (type: MigrationType) => {
    // Wizards assume the source→target pair was chosen (and permission-checked) on the
    // select-instances step — never enter one without it.
    const { sourceServiceId, targetServiceId } = useInstancePairStore.getState();
    if (sourceServiceId == null || targetServiceId == null) {
      toast({
        variant: "destructive",
        title: "Select instances first",
        description: "Choose a source and target instance before starting a migration.",
      });
      navigate("/select-instances");
      return;
    }

    try {
      toast({
        title: "Creating workflow...",
        description: "Please wait while we set up your migration workflow.",
      });

      // Reset any existing workflow state first to ensure a clean start.
      // This prevents issues with persisted state from previous workflows.
      useWorkflowStore.getState().resetForm();

      // Generate a code for the workflow.
      const codeData = await generateCode(type);

      // Extract the code and name from the response.
      const { code, name } = codeData as { code: string; name: string };

      // Set the current workflow in the store with the generated code and name.
      setCurrentWorkflow({ id: code, name, type });

      // Navigate directly to the workflow detail page using the generated code.
      navigate(`/workflows/${ROUTE_SEGMENT[type]}/${code}`);
    } catch (error) {
      console.error("Error creating workflow:", error);

      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create workflow. Please try again.",
      });
    }
  };

  return {
    isGenerating,
    startOntology: () => start("ONTOLOGY"),
    startProcess: () => start("PROCESS"),
    startActionEffect: () => start("ACTION_EFFECT"),
  };
}
